import { inject, Injectable } from "@angular/core";
import { MonitoringCategory, MonitoringProductService } from "@domain/product/monitoring/monitoring-product.service";
import { ProductImage } from "@domain/product/product-image";
import { ProductService } from "@domain/product/product.service";
import { CentralStationConfiguration } from "@domain/project/configurations/monitoring/central-station-configuration";
import { PatientMonitorConfiguration } from "@domain/project/configurations/monitoring/patient-monitor-configuration";
import { TelemetryConfiguration } from "@domain/project/configurations/monitoring/telemetry-configuration";
import { catchError, map, Observable, of, switchMap } from "rxjs";

@Injectable({ providedIn: "root" })
export class MonitoringProductImageService {
  private readonly monitoringProductService = inject(MonitoringProductService);
  private readonly productService = inject(ProductService);

  resolvePatientMonitorImageUrl(config: PatientMonitorConfiguration): Observable<string> {
    const devices = config.family?.option.devices ?? [];
    const fallbackUrl = ProductImage.getCantoSourceUrl(config.family?.option.groupImageCantoID ?? "");

    if (devices.length !== 1) return of(fallbackUrl);

    return this.monitoringProductService
      .loadDeviceFromDB(devices[0], MonitoringCategory.PATIENT_MONITORS, this.productService.getFilterCountry())
      .pipe(
        map((device) => (device?.productImageCantoID ? ProductImage.getCantoSourceUrl(device.productImageCantoID) : fallbackUrl)),
        catchError(() => of(fallbackUrl)),
      );
  }

  resolveCentralStationImageUrl(config: CentralStationConfiguration): Observable<string> {
    const devices = config.baseOptions.flatMap((item) => item.option.devices);

    if (devices.length === 1) {
      return this.monitoringProductService
        .loadDeviceFromDB(devices[0], MonitoringCategory.CENTRAL_STATIONS, this.productService.getFilterCountry())
        .pipe(
          switchMap((device) =>
            device?.productImageCantoID
              ? of(ProductImage.getCantoSourceUrl(device.productImageCantoID))
              : this.resolveCentralStationFamilyImageUrl(config),
          ),
          catchError(() => this.resolveCentralStationFamilyImageUrl(config)),
        );
    }

    return this.resolveCentralStationFamilyImageUrl(config);
  }

  resolveTelemetryImageUrl(config: TelemetryConfiguration): Observable<string> {
    const deviceId = config.family?.[0]?.option.devices[0];
    if (!deviceId) return of("");

    return this.monitoringProductService
      .loadDeviceFromDB(deviceId, MonitoringCategory.TELEMETRY, this.productService.getFilterCountry())
      .pipe(
        map((device) => (device?.productImageCantoID ? ProductImage.getCantoSourceUrl(device.productImageCantoID) : "")),
        catchError(() => of("")),
      );
  }

  private resolveCentralStationFamilyImageUrl(config: CentralStationConfiguration): Observable<string> {
    const configuredFamilyImageId = config.family?.option.groupImageCantoID;
    if (configuredFamilyImageId) return of(ProductImage.getCantoSourceUrl(configuredFamilyImageId));

    return this.monitoringProductService
      .loadFamilyFromDB(MonitoringCategory.CENTRAL_STATIONS, config.familyNumber, this.productService.getFilterCountry())
      .pipe(
        map((family) => (family?.familyImageCantoId ? ProductImage.getCantoSourceUrl(family.familyImageCantoId) : "")),
        catchError(() => of("")),
      );
  }
}
